import { computed, type ComputedRef } from 'vue'
import { parseLocal } from '~/composables/support/localization'
import { differenceInHours } from 'date-fns'
import type {
    BookingStatusAvailableResult,
    CourseBookingStatus
} from '~/resources/graphql/graphql'

export enum WaitlistMode {
    None,
    Available,
    Closed,
    Full
}

export function useWaitlistMode(
    bookingLimit: ComputedRef<{
        waitlistEnabled: boolean
        hoursBeforeCourseWaitlistCloses: number | null
    }>,
    selectedCourse: ComputedRef<{
        boundsStartLocal: string
        bookingStatus: CourseBookingStatus
        bookingStatusAvailable: BookingStatusAvailableResult[]
        capacityResult: {
            isWaitlistFull: boolean
            overlappedBookedSessions: number
            contracts: number
            enrollments: number
        }
    } | null>
) {
    return computed(() => {
        // If waitlist is not enabled for plan
        if (bookingLimit.value.waitlistEnabled === false) {
            return WaitlistMode.None
        }

        // If no course has been selected yet
        if (selectedCourse.value === null) {
            return WaitlistMode.None
        }

        // If there is capacity remaining, allow for booking
        if (
            selectedCourse.value.capacityResult.overlappedBookedSessions > 0 &&
            selectedCourse.value.capacityResult.contracts > 0 &&
            selectedCourse.value.capacityResult.enrollments > 0
        ) {
            if (selectedCourse.value.bookingStatus === 'BOOKING') {
                return WaitlistMode.None
            } else if (selectedCourse.value.bookingStatus === 'CLOSED') {
                return WaitlistMode.None
            } else if (selectedCourse.value.bookingStatus === 'WAITLIST') {
                // If this customer can use their waitlist spot - no waitlist mode
                if (selectedCourse.value.bookingStatusAvailable.length > 0) {
                    return WaitlistMode.None
                }

                // Otherwise, fall through to waitlist checks
            } else {
                // Unknown booking status
                throw new Error()
            }
        }

        // If the waitlist has closed relative to the course start
        if (bookingLimit.value.hoursBeforeCourseWaitlistCloses !== null) {
            const courseStart = parseLocal(
                selectedCourse.value.boundsStartLocal
            )
            if (
                differenceInHours(courseStart, new Date()) <
                bookingLimit.value.hoursBeforeCourseWaitlistCloses
            ) {
                return WaitlistMode.Closed
            }
        }

        // If the selected course waitlist is full
        if (selectedCourse.value.capacityResult.isWaitlistFull) {
            return WaitlistMode.Full
        }

        // If this customer can use their waitlist spot - no waitlist mode
        if (
            selectedCourse.value.bookingStatusAvailable.some(
                status => status.overrideCapacityConstraints
            )
        ) {
            return WaitlistMode.None
        }

        return WaitlistMode.Available
    })
}
